import { useMemo } from 'react';

import {
	type StarterDashboardV2PageData,
	useStarterDashboardV2PageData,
} from '#src/app/pages/starterDashboardV2/pageContext';
import { useShareVisualModalBody } from '#src/app/pages/starterDashboardV2/shared/hooks';
import type { ShareVisualImg } from '#src/app/pages/starterDashboardV2/shared/types';
import { useLocale } from '#src/app/shared/hooks/l10n';

type Context = 'starter_dashboard_grow_petition';
export type ShareVisualData = {
	buttonDisabled: boolean;
	newModuleEnabled: boolean;
	shareVisualDownloaded: boolean;
	shareVisualEnabled: boolean;
	shareVisualsState: 'loading' | 'loaded' | 'timeout';
	petition: StarterDashboardV2PageData['petition'];
	items: ShareVisualImg[];
};

type UseShareVisualReturn = ModelHookResult<
	ShareVisualData,
	{
		onShareVisualDownload: (event: React.FormEvent<HTMLFormElement>) => void;
		onShareVisualSelect: () => void;
	}
>;

export function useShareVisual(): UseShareVisualReturn {
	const {
		data: {
			petition,
			featureConfigs: { starterDashboardNewAssetsConfig, shareVisualsConfig },
		},
	} = useStarterDashboardV2PageData();
	const context: Context = 'starter_dashboard_grow_petition';
	const {
		data,
		actions: { onShareVisualDownload, onShareVisualSelect },
	} = useShareVisualModalBody({ petition, context });
	const locale = useLocale();

	const shareVisual = useMemo(() => {
		const disabled = { enabled: false } as const;
		if (!shareVisualsConfig) return disabled;
		if (!shareVisualsConfig.enabled) return disabled;
		if (!shareVisualsConfig.locales.includes(locale)) return disabled;
		return { enabled: true } as const;
	}, [locale, shareVisualsConfig]);

	const newAssetsEnabled = useMemo(() => {
		const disabled = { enabled: false } as const;
		if (!starterDashboardNewAssetsConfig) return disabled;
		if (!starterDashboardNewAssetsConfig.enabled) return disabled;
		if (!starterDashboardNewAssetsConfig.locales.includes(locale)) return disabled;

		return { enabled: true } as const;
	}, [locale, starterDashboardNewAssetsConfig]);

	return {
		data: {
			petition,
			buttonDisabled: data.disabled,
			newModuleEnabled: newAssetsEnabled.enabled,
			shareVisualDownloaded: data.shareVisualDownloaded,
			shareVisualEnabled: shareVisual.enabled,
			shareVisualsState: data.shareVisualsState,
			items: data.shareVisuals,
		},
		actions: {
			onShareVisualDownload,
			onShareVisualSelect,
		},
	};
}
